import React, { Component } from "react";
import { Link } from "react-router-dom";
import '../../css/table-responsive.css'
import '../../css/dashboard.css'
import {
  getCurrentBalance, getCustomerExcess, getCustomerHistory, getSalesByCustomer,
  getSalesOrdersPerUser, getSalesPerMonth, getSalesPerRoute, getSalesPerSalesPerson,
  getSalesPerRouteAllYear,
} from "./Functions";
import { NumberFormat } from "../../functions/generalFunctions";
import LoadingWindow from "../../components/LoadingWindow";
import DashTotalsView from "./components/DashTotalsView/DashTotalsView";
import { Menu } from "../../components/Menu/Menu";

/**
 * AdminDashboardTotals.js
 *
 * 2026-09-04, client-reported bug (Altitude): the "Totals" tab inside
 * AdminLobbyV2.js showed Mayaland's own revenue and Mayaland's own
 * salespeople instead of Altitude's — traced to getSalesPerRoute/
 * getSalesPerSalesPerson/getSalesByCustomer (InitialDashBoard/Functions.js)
 * all calling a backend endpoint hardcoded to Mayaland's mirror tables with
 * no idCompany awareness at all.
 *
 * That file — and every component DashTotalsView composes — is shared with
 * SalesLobbyV2.js, the individual-salesperson dashboard another developer is
 * actively fixing in parallel. Editing any of it risked breaking the
 * individual-salesperson tabs (confirmed working for Altitude) and
 * colliding with that concurrent work. Client's explicit ask: clone
 * everything into its own isolated page/URL instead of touching the shared
 * tab-based Totals view — see this file's own Functions.js and
 * components/ for the corresponding clones.
 *
 * Mayaland's existing Totals tab (AdminLobbyV2.js) is completely untouched —
 * nothing here is imported by it, and this page doesn't touch any Mayaland
 * code path either (idCompany is read from the same cookie every other page
 * uses, so a Mayaland admin who somehow lands here would still see their own
 * correct data via the same already-correct backend endpoints, but nothing
 * about their existing dashboard changes because of this page's existence).
 *
 * This page renders ONLY the Totals view — no per-salesperson tabs, no
 * Altitude paid-invoices widget (AdminLobbyV2.js-specific, unrelated to
 * Totals) — just the single 'TOT' pseudo-salesperson dataset DashTotalsView
 * needs.
 */
export default class AdminDashboardTotals extends Component {

  constructor(props) {
    super(props);
    this.ModalLoading = React.createRef();
  }

  state = {
    element: {
      SalesPerson: 'TOT',
      SalesPerRouteDate: new Date(),
      SalesPerRoute: { Information: [], Months: [] },
      SalesPerSalesPersons: { Information: [], Months: [] },
      CustomerHistory: [],
      CustomerExcess: [],
      CurrentBalance: [],
      SalesByCustomer: [],
      SalesPerMonth: [],
      RouteYearSales: [],
      Totals: {
        TotalPendingInvoices: { TotalDueInv: 0, Range1: 0, Range2: 0, Range3: 0, TotalR1: 0, TotalR2: 0, TotalR3: 0 },
        SalesOrder: { NotStarted: 0, InProcess: 0, Finished: 0 },
        Customer: { Normal: 0, Frequent: 0, Waring: 0, Risk: 0, Total: 0 },
        Sales: { NoInvoices: 0, Total: 0 }
      }
    }
  }

  componentDidMount() {
    this.loadAll();
  }

  async loadAll() {
    this.ModalLoading.current.showState(true);
    try {
      await Promise.all([
        this.loadSalesPerRoute(),
        this.loadPendingSalesOrder(),
        this.loadCustomerExcess(),
        this.loadCurrentBalance(),
        this.loadCustomerHistory(),
        this.loadSalesByCustomer(),
        this.loadSalesPerMonth(),
        this.loadSalesPerSalesPersons(),
        this.loadRouteYearSales(),
      ]);
    } finally {
      this.ModalLoading.current.showState(false);
    }
  }

  async loadSalesPerRoute() {
    try {
      const SalesPerRoute = await getSalesPerRoute('TOT');
      this.setState(prev => ({ element: { ...prev.element, SalesPerRoute, SalesPerRouteDate: new Date(), SalesPerRouteLoading: false, SalesPerRouteError: null } }));
    } catch (e) {
      this.setState(prev => ({ element: { ...prev.element, SalesPerRouteLoading: false, SalesPerRouteError: 'Can not get data from the API. ' } }));
    }
  }

  async loadPendingSalesOrder() {
    try {
      const PendingSalesOrder = await getSalesOrdersPerUser('TOT');
      let NotStarted = 0, InProcess = 0, Finished = 0;
      for (const SalesOrder of PendingSalesOrder) {
        SalesOrder.Status = SalesOrder.Status ? SalesOrder.Status : 0;
        const amt = Number(SalesOrder.NonTaxableAmt) || 0;
        switch (SalesOrder.Status) {
          case 0: NotStarted += amt; break;
          case 1: InProcess += amt; break;
          case 2: Finished += amt; break;
          default: break;
        }
      }
      this.setState(prev => ({
        element: { ...prev.element, PendingSalesOrders: PendingSalesOrder, Totals: { ...prev.element.Totals, SalesOrder: { NotStarted, InProcess, Finished } } }
      }));
    } catch (e) { /* non-blocking — see AdminLobbyV2.js's own reasoning for this section */ }
  }

  async loadCustomerExcess() {
    try {
      const CustomerExcess = await getCustomerExcess('TOT');
      this.setState(prev => ({ element: { ...prev.element, CustomerExcess, CustomerExcessLoading: false, CustomerExcessError: null } }));
    } catch (e) {
      this.setState(prev => ({ element: { ...prev.element, CustomerExcessLoading: false, CustomerExcessError: 'Can not get data from the API. ' } }));
    }
  }

  async loadCurrentBalance() {
    try {
      const CurrentBalance = await getCurrentBalance('TOT');
      const TotalPendingInvoices = { TotalDueInv: 0, Range1: 0, Range2: 0, Range3: 0, TotalR1: 0, TotalR2: 0, TotalR3: 0 };
      for (const Invoice of CurrentBalance) {
        const balance = Invoice.Balance || 0;
        const days = Number(Invoice.DaysDlq);
        if (balance > 0) TotalPendingInvoices.TotalDueInv += balance;
        if (days <= 14) {
          TotalPendingInvoices.Range1 += 1;
          if (balance > 0) TotalPendingInvoices.TotalR1 += balance;
        } else if (days >= 15 && days <= 29) {
          TotalPendingInvoices.Range2 += 1;
          if (balance > 0) TotalPendingInvoices.TotalR2 += balance;
        } else {
          TotalPendingInvoices.Range3 += 1;
          if (balance > 0) TotalPendingInvoices.TotalR3 += balance;
        }
      }
      this.setState(prev => ({
        element: { ...prev.element, CurrentBalance, CurrentBalanceLoading: false, CurrentBalanceError: null, Totals: { ...prev.element.Totals, TotalPendingInvoices } }
      }));
    } catch (e) {
      this.setState(prev => ({ element: { ...prev.element, CurrentBalanceLoading: false, CurrentBalanceError: 'Can not get data from the API. ' } }));
    }
  }

  async loadCustomerHistory() {
    try {
      const CustomerHistory = await getCustomerHistory('TOT');
      this.setState(prev => ({ element: { ...prev.element, CustomerHistory, CustomerHistoryLoading: false, CustomerHistoryError: null } }));
    } catch (e) {
      this.setState(prev => ({ element: { ...prev.element, CustomerHistoryLoading: false, CustomerHistoryError: 'Can not get data from the API. ' } }));
    }
  }

  async loadSalesByCustomer() {
    try {
      const SalesByCustomer = await getSalesByCustomer('TOT', null);
      this.setState(prev => ({ element: { ...prev.element, SalesByCustomer } }));
    } catch (e) { /* SalesByCustomer has no its own error banner in DashTotalsView, matches original behavior */ }
  }

  async loadSalesPerMonth() {
    try {
      const SalesPerMonth = await getSalesPerMonth('TOT', null);
      let TotSales = { NoInvoices: 0, Total: 0 };
      for (const Sale of SalesPerMonth) {
        if (Sale.Week) {
          TotSales.NoInvoices += Number(Sale.totalsales)
          TotSales.Total += Number(Sale.total)
        }
      }
      this.setState(prev => ({ element: { ...prev.element, SalesPerMonth, Totals: { ...prev.element.Totals, Sales: TotSales } } }));
      this.asignTotalsCalendar(SalesPerMonth);
    } catch (e) { /* Sales KPI simply stays at its initial zero state on failure */ }
  }

  async loadSalesPerSalesPersons() {
    try {
      const SalesPerSalesPersons = await getSalesPerSalesPerson('TOT', null);
      this.setState(prev => ({ element: { ...prev.element, SalesPerSalesPersons } }));
    } catch (e) { /* table simply stays empty on failure */ }
  }

  async loadRouteYearSales() {
    try {
      const RouteYearSales = await getSalesPerRouteAllYear();
      this.setState(prev => ({ element: { ...prev.element, RouteYearSales } }));
    } catch (e) { /* purely visual — never blocks or errors the rest of the page */ }
  }

  asignTotalsCalendar(Data) {
    for (const week of Data) {
      if (week.Week) {
        let m = document.getElementById('quant_' + week.Week + "_TOT")
        let n = document.getElementById('sales_' + week.Week + "_TOT")
        if (m) m.innerHTML = NumberFormat(Number(Number(week.totalsales).toFixed(0)))
        if (n) n.innerHTML = "$" + NumberFormat(Number(Number(week.total).toFixed(0)))
      }
    }
  }

  async changeMonthSalesPerRoute(SalesPerson, DateC) {
    this.ModalLoading.current.showState(true);
    const SalesPerRoute = await getSalesPerRoute(SalesPerson, new Date(DateC))
    const SalesByCustomer = await getSalesByCustomer(SalesPerson, new Date(DateC))
    this.setState(prev => ({ element: { ...prev.element, SalesPerRoute, SalesByCustomer } }));
    this.ModalLoading.current.showState(false);
  }

  async changeMonthSalesPerSalesPerson(SalesPerson, DateC) {
    this.ModalLoading.current.showState(true);
    const SalesPerSalesPersons = await getSalesPerSalesPerson(SalesPerson, new Date(DateC))
    this.setState(prev => ({ element: { ...prev.element, SalesPerSalesPersons } }));
    this.ModalLoading.current.showState(false);
  }

  async changeDateSalesPerMonth(SalesPerson, DateC) {
    this.ModalLoading.current.showState(true);
    const SalesPerMonth = await getSalesPerMonth(SalesPerson, new Date(DateC))
    let TotSales = { NoInvoices: 0, Total: 0 }
    for (const Sale of SalesPerMonth) {
      if (Sale.Week) {
        TotSales.NoInvoices += Number(Sale.totalsales)
        TotSales.Total += Number(Sale.total)
      }
    }
    this.setState(prev => ({ element: { ...prev.element, SalesPerRouteDate: new Date(DateC), SalesPerMonth, Totals: { ...prev.element.Totals, Sales: TotSales } } }));
    this.asignTotalsCalendar(SalesPerMonth)
    this.ModalLoading.current.showState(false);
  }

  // Father.changeAllMonthData — the single callback TableSalesTypeView,
  // CalendarOfSales and TableSalesPerSalesPersonView call for month
  // navigation (see this file's header comment).
  async changeAllMonthData(SalesPerson, DateC) {
    await this.changeMonthSalesPerRoute(SalesPerson, DateC)
    await this.changeDateSalesPerMonth(SalesPerson, DateC)
    await this.changeMonthSalesPerSalesPerson(SalesPerson, DateC)
  }

  render() {
    return (
      <div className="dash-page" style={{ paddingTop: '70px', minHeight: '100vh', background: '#F8FAFC' }}>
        <Menu />
        <LoadingWindow ref={this.ModalLoading} />
        <div className="container-fluid pb-5">
          {/* 2026-09-04: this page has no salesperson tabs (Totals-only, by
              design — see header comment), so without this link an Altitude
              admin had no way back to their own per-salesperson tabs. */}
          <Link to="/admin/dashboard" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            fontSize: '0.85rem', fontWeight: 700, color: '#2563EB',
            textDecoration: 'none', marginTop: '1rem', marginBottom: '0.5rem',
          }}>
            ← Back to Dashboard
          </Link>
          <DashTotalsView element={this.state.element} Father={this} />
        </div>
      </div>
    );
  }
}
